import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import flexControlsClient from '@clients/domains/flexControls/flexControlsClient';
import dontRetry404 from '@utils/dontRetry404';
import is404Response from '@utils/is404Response';
import notifyAPIError from '@utils/notifyAPIError';

const { getFlexControls, getCustomerFlexControls } = flexControlsClient();

const useGetFlexControls = (accountId: number, customerId: number | null) => {
    // If we don't have a customerId get account controls
    const { data, error, isError, isLoading, isFetching } = useQuery(
        ['getFlexControls', accountId],
        () => getFlexControls({ accountId }),
        {
            onError: (error) => {
                if (!is404Response((error as AxiosError).response)) {
                    notifyAPIError(error);
                }
            },
            retry: dontRetry404,
            enabled: !!accountId && !customerId,
        },
    );

    // If we do have a customerId get the customer controls
    const {
        data: customerData,
        error: customerError,
        isError: isCustomerError,
        isLoading: isCustomerLoading,
        isFetching: isCustomerFetching,
    } = useQuery(
        ['getCustomerFlexControls', accountId, customerId],
        () => getCustomerFlexControls({ customerId, accountId }),
        {
            onError: (error) => {
                if (!is404Response((error as AxiosError).response)) {
                    notifyAPIError(error);
                }
            },
            retry: dontRetry404,
            enabled: !!customerId,
        },
    );

    return {
        // If we have a customerId and have just done a request for the customer flex controls return that data.
        data: customerId ? customerData : data,
        error: customerId ? customerError : error,
        isError: isError || isCustomerError,
        isLoading: isLoading || isCustomerLoading,
        isFetching: isFetching || isCustomerFetching,
    };
};

export default useGetFlexControls;
